import { useState, useCallback, useContext, useRef, useEffect } from 'react';
import { InvisibleSmartCaptcha } from '@yandex/smart-captcha';
import { PopupContext } from '../../Popup';
import { Link } from 'react-router-dom';
// import { PATHS } from '../../../../../constants/routes';
import toast from 'react-hot-toast';
import { getCsrfToken } from '../../../../../utils/helpers';
import './feedback.scss';

const PHONE_REGEX = /^\+7\s\(\d{3}\)\s\d{3}-\d{2}-\d{2}$/;
const CAPTCHA_CLIENT_KEY = process.env.REACT_APP_YANDEX_CAPTCHA_CLIENT_KEY ?? '';
const BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5000';

const Feedback = () => {
    const { onClose } = useContext(PopupContext);
    const [captchaVisible, setCaptchaVisible] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [message, setMessage] = useState('');
    const [agree, setAgree] = useState(false);
    const phoneRef = useRef<HTMLInputElement>(null);

    const [touched, setTouched] = useState({
        name: false,
        phone: false,
        message: false,
        agree: false,
    });

    const hasNameError = touched.name && !name.trim();
    const hasPhoneError = touched.phone && !PHONE_REGEX.test(phone);
    const hasMessageError = touched.message && !message.trim();
    const hasAgreeError = touched.agree && !agree;
    const isValid = !hasNameError && !hasPhoneError && !hasMessageError && agree;

    useEffect(() => {
        if (!phoneRef.current) return;
        let im: any;
        import('inputmask').then(({ default: Inputmask }) => {
            im = new Inputmask({
                mask: '+7 (999) 999-99-99',
                placeholder: '_',
                showMaskOnHover: false,
                clearIncomplete: true,
            });
            im.mask(phoneRef.current!);
        });
        return () => {
            if (im) im.remove();
        };
    }, []);

    const handleCaptchaSuccess = useCallback(
        async (token: string) => {
            setCaptchaVisible(false);
            setIsSubmitting(true);
            try {
                const csrf = await getCsrfToken();
                const res = await fetch(`${BASE_URL}/feedback`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json', 'csrf-token': csrf },
                    body: JSON.stringify({ name, phone, message, captchaToken: token }),
                });
                if (res.ok) {
                    toast.success('Ваше сообщение отправлено');
                    onClose();
                } else {
                    const data = await res.json().catch(() => ({}));
                    toast.error(data?.error || 'Ошибка при отправке');
                }
            } catch {
                toast.error('Не удалось связаться с сервером');
            } finally {
                setIsSubmitting(false);
            }
        },
        [name, phone, message, onClose],
    );

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setTouched({ name: true, phone: true, message: true, agree: true });
        if (isValid) setCaptchaVisible(true);
    };

    return (
        <div className="popup-feedback">
            <div className="popup-feedback__contentLeft">
                <h2 className="popup-feedback__contentLeft_title">Есть идея или вопрос? Давайте обсудим</h2>
                <div className="popup-feedback__contentLeft_contacts">
                    <div className="popup-feedback__contentLeft_contacts_container">
                        <a href={'tel:+74952806055'} className="popup-feedback__contentLeft_contacts_container_value">
                            +7-495-280-60-55
                        </a>
                        <span className="popup-feedback__contentLeft_contacts_container_label">Служба поддержки клиентов</span>
                    </div>
                    <div className="popup-feedback__contentLeft_contacts_container">
                        <a href={'mailto:sales@s-gabai.ru'} className="popup-feedback__contentLeft_contacts_container_value">
                            sales@s-gabai.ru
                        </a>
                        <span className="popup-feedback__contentLeft_contacts_container_label">По вопросам сотрудничества</span>
                    </div>
                </div>
            </div>
            <form action="" className="popup-feedback__contentRight" onSubmit={handleSubmit} noValidate>
                <div className="popup-feedback__contentRight_group">
                    <input
                        id="name"
                        className={`popup-feedback__contentRight_group_input ${hasNameError ? 'popup-feedback__contentRight_group_input_error' : ''}`}
                        type="text"
                        placeholder="Ваше имя"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                        maxLength={50}
                    />
                    <div className={`popup-feedback__contentRight_group_errorWrapper ${hasNameError ? 'popup-feedback__contentRight_group_errorWrapper_show' : ''}`}>
                        {hasNameError && <span className="popup-feedback__contentRight_group_error">Введите имя</span>}
                    </div>
                </div>
                <div className="popup-feedback__contentRight_group">
                    <input
                        id="phone"
                        ref={phoneRef}
                        className={`popup-feedback__contentRight_group_input ${hasPhoneError ? 'popup-feedback__contentRight_group_input_error' : ''}`}
                        type="tel"
                        placeholder="+7 (___) ___-__-__"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
                    />
                    <div
                        className={`popup-feedback__contentRight_group_errorWrapper ${hasPhoneError ? 'popup-feedback__contentRight_group_errorWrapper_show' : ''}`}
                    >
                        {hasPhoneError && <span className="popup-feedback__contentRight_group_error">Введите номер телефона</span>}
                    </div>
                </div>
                <div className="popup-feedback__contentRight_group">
                    <textarea
                        id="message"
                        className={`popup-feedback__contentRight_group_textarea ${hasMessageError ? 'popup-feedback__contentRight_group_textarea_error' : ''}`}
                        placeholder="Ваше сообщение"
                        rows={10}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onBlur={() => setTouched((t) => ({ ...t, message: true }))}
                        maxLength={300}
                    />
                    <div
                        className={`popup-feedback__contentRight_group_errorWrapper ${
                            hasMessageError ? 'popup-feedback__contentRight_group_errorWrapper_show' : ''
                        }`}
                    >
                        {hasMessageError && <span className="popup-feedback__contentRight_group_error">Введите сообщение</span>}
                    </div>
                </div>
                <div className="popup-feedback__contentRight_group">
                    <div className="popup-feedback__contentRight_group_checkbox">
                        <input
                            id="agree"
                            className={`popup-feedback__contentRight_group_checkbox_input ${
                                hasAgreeError ? 'popup-feedback__contentRight_group_checkbox_input_error' : ''
                            }`}
                            type="checkbox"
                            checked={agree}
                            onChange={(e) => {
                                setAgree(e.target.checked);
                                setTouched((t) => ({ ...t, agree: true }));
                            }}
                        />
                        <label className="popup-feedback__contentRight_group_checkbox_label" htmlFor="agree">
                            Я ознакомлен(а) и согласен(на) с&nbsp;
                            <Link
                                className="popup-feedback__contentRight_group_checkbox_label_link"
                                to="/customers?section=policy"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                политикой конфиденциальности
                            </Link>
                        </label>
                    </div>
                    <div
                        className={`popup-feedback__contentRight_group_errorWrapper ${hasAgreeError ? 'popup-feedback__contentRight_group_errorWrapper_show' : ''}`}
                    >
                        {hasAgreeError && <span className="popup-feedback__contentRight_group_error">Необходимо Ваше согласие</span>}
                    </div>
                </div>
                <button className="popup-feedback__contentRight_button" type="submit" disabled={!isValid || isSubmitting || captchaVisible}>
                    {isSubmitting ? 'Отправка...' : 'Отправить'}
                </button>
                {captchaVisible && (
                    <div className="popup-feedback__contentRight_captcha">
                        <InvisibleSmartCaptcha sitekey={CAPTCHA_CLIENT_KEY} onSuccess={handleCaptchaSuccess} hideShield visible={true} />
                    </div>
                )}
            </form>
        </div>
    );
};

export default Feedback;
